import * as React                    from 'react';

import { useAlert                  } from 'react-alert';

import { v4 as uuidv4              } from 'uuid';

import Drawer                        from '@mui/material/Drawer';

import { PhotoProvider             } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

import { ArrowBackVector           } from 'src/components/vector/arrow-back';
import { ArrowDownVector           } from 'src/components/vector/arrow-down';
import { TrashVector               } from 'src/components/vector/trash';

import { IconButton                } from 'src/components/common/icon-button';
import { Button                    } from 'src/components/common/button';
import { Checkbox                  } from 'src/components/common/checkbox';
import { RadioGroup,
         RadioGroupItem            } from 'src/components/common/radio-group';
import { Typography                } from 'src/components/common/typography';
import { Labeled                   } from 'src/components/common/labeled';
import { InputTextbox              } from 'src/components/common/input/input-textbox';
import { InputMultiline            } from 'src/components/common/input/input-multiline';
import { InputPhone                } from 'src/components/common/input/input-phone';
import { Grow                      } from 'src/components/common/flex/grow';
import { TwoActionDialog           } from 'src/components/common/two-action-dialog';

import { ReferSelector, 
         ReferSelectorItem         } from 'src/components/features/common/refer-selector';

import { AttachmentAdd             } from 'src/components/features/inspection/edit/attachment-add';
import { AttachmentDOC             } from 'src/components/features/inspection/edit/attachment-doc';

import { ComplaintEditLookAndFeel, 
         Container,
         HeaderContainer,
         FormContainer,
         SubmitContainer,
         PhotoList,
         Hr,
         RowWithAction,
         CloseOutlineVectorBig, 
         ComplaintEditVariants,
         complaintedit_variants    } from 'src/components/features/inspection/complaint/complaint-edit-dialog/ui';

import { Urls                      } from 'src/providers/routing';

import * as types                    from 'src/services/api/types';
import Util                          from 'src/services/util';
import { ComplaintType,
         DefectType                } from 'src/services/api/types/inspection';
import { AccountRole               } from 'src/services/api/types/auth';
import Constants                     from 'src/services/constants';

import { AppDispatch,
         useAppSelector, 
         useAppDispatch,
         storeApi                  } from 'src/store';

import { voidFunction              } from 'src/common';



const MODE_DEL = types.inspection.DefectFileMode.DEL;
const ADDITIONAL_COSTS = types.inspection.DefectFileType.ADDITIONAL_COSTS;
const MODE_NEW = types.inspection.DefectFileMode.NEW;

const SAVE_NOW_TRUE = { isSaveEditedPiece: false, isSaveEditedComplaint: true };
const SAVE_NOW_TRUE_APPLY = { isSaveEditedPiece: false, isSaveEditedComplaint: true, isApplyEditedComplaint: true };
const SAVE_NOW_FALSE = { isSaveEditedPiece: false, isSaveEditedComplaint: false };

type ComplaintEditMode = 'new' | 'edit' | 'tocomplaint' | 'tocomplaintfinance' | 'costs' | 'all' | 'view';

type ComplaintEditFormElement = { lookAndFeel: ComplaintEditLookAndFeel; dispatch: AppDispatch; mode: ComplaintEditMode };

const Type: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const defectGuid = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintDefectGuid);
  const defectId = useAppSelector((store) => storeApi.inspection.edit.selectors.selectDefectDefectId(store, defectGuid));
  const defectRef = useAppSelector(storeApi.inspection.edit.selectors.selectGeneralDefectRefer);
  const defect = defectRef.find((item) => item.id === defectId);

  const markOnlyClaim = defect?.configs?.find((conf) => conf.code === 'ONLY_CLAIM')?.value ?? '0';
  const markOnlyRemark = defect?.configs?.find((conf) => conf.code === 'ONLY_REMARK')?.value ?? '0';
  const markProhibitionOfRemarks = defect?.configs?.find((conf) => conf.code === 'PROHIBITION_OF_REMARKS')?.value ?? '0';

  const isOnlyClaim = markOnlyClaim === '1' || markProhibitionOfRemarks === '1';
  const isOnlyRemark = markOnlyRemark === '1';

  let items: RadioGroupItem[] = [];

  switch (mode)
  {
    case 'tocomplaint':
    case 'tocomplaintfinance':
    case 'costs':
    case 'all':
      items = [{ id: 'complaint', label: 'Рекламация' }];
      break;
    case 'view':
      items = complaintType === ComplaintType.COMPLAINT ? [{ id: 'complaint', label: 'Рекламация' }] : [{ id: 'remark', label: 'Замечание' }];
      break;
    default:
      items = [{ id: 'remark', label: 'Замечание' }, { id: 'complaint', label: 'Рекламация' }];
  }

  const item = complaintType === ComplaintType.REMARK ?
    items.find((item) => item.id === 'remark')!
    :
    items.find((item) => item.id === 'complaint')!;

  const onChangeHandler = (value: RadioGroupItem) => {
    if (['new', 'edit'].includes(mode))
    {
      dispatch(storeApi.inspection.edit.actions.complaintEditedTypeComplaintChanged(
        value.id === 'remark' ? ComplaintType.REMARK : ComplaintType.COMPLAINT
      ));
      setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
    }
  }

  return (
    <RadioGroup
      variant = 'light'
      items = { items }
      value = { item }
      isDisabled = { mode === 'view' || isOnlyClaim || isOnlyRemark }
      onChange = { onChangeHandler }
    />
  )
}

const IsToScrap: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isToScrap = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsToScrap);
  const enterpriseId = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedInitiatorEnterpriseId);
  const userFilialId = useAppSelector(storeApi.inspection.edit.selectors.selectGeneralUserFilialId);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsToScrapChanged(value));

    if (value)
    {
      /**
       * Если передаем конкретный enterpriseId, то филиал передаем null.
       * Если не передаем конкретный enterpriseId, то филиал передаем конкретный (если он есть)
       * или null (если новый осмотр)
       */
      const argEnterpriseId = enterpriseId > -1 ? enterpriseId : null;
      const argFilialId = argEnterpriseId === null ? ((userFilialId ?? 0) > 0 ? userFilialId : null) : null;

      dispatch(storeApi.inspection.edit.async.replaceScrapPriceAsync({
        enterpriseId: argEnterpriseId,
        filialId: argFilialId
      }))
        .unwrap()
        .then(() => {
          setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
        })
        .catch(() => {
          setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
        })
    }
    else
    {
      setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
    }
  }

  if (complaintType === ComplaintType.REMARK)
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Уценить металлопрокат до цены лома'
      value = { isToScrap ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const ScrapPrice: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isToScrap = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsToScrap);
  const scrapPrice = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedScrapPrice);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedScrapPriceChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK || !isToScrap)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } isRequired label = 'Цена лома (руб./т без НДС)'>
      <InputTextbox
        placeholder = 'Цена лома'
        variant = { lookAndFeel.textboxVariant }
        value = { scrapPrice !== null ? `${scrapPrice}` : '' }
        valueTransformer = { (value: string) => Util.selectDigitsAndDotFromStringN_M(value, 5, 2) }
        maxLength = { 10 }
        isDisabled = { ['view', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const IsEvaluateRolledMetal: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isEvaluateRolledMetal = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsEvaluateRolledMetal);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsEvaluateRolledMetalChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK)
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Уценить металлопрокат'
      value = { isEvaluateRolledMetal ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const RequirementRolledMetall: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isEvaluateRolledMetal = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsEvaluateRolledMetal);
  const requirementRolledMetall = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedRequirementRolledMetall);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedRequirementRolledMetallChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK || !isEvaluateRolledMetal)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Укажите требование к уценке'>
      <InputMultiline
        placeholder = 'Требования к уценке'
        variant = { lookAndFeel.multilineVariant }
        value = { requirementRolledMetall !== null ? `${requirementRolledMetall}` : '' }
        isDisabled = { ['view', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const SumReqRolledMetall: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isEvaluateRolledMetal = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsEvaluateRolledMetal);
  const sumReqRolledMetall = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedSumReqRolledMetall);

  const initiatorAOSDSelected = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedInitiatorAOSDSelected);
  const account = useAppSelector(storeApi.auth.common.selectors.selectAccount)!;
  const isAOSD = account.configs.find((item) => item.code === 'IND_DISTRIBUTION')!.value === '1';

  const isRequired = !isAOSD || initiatorAOSDSelected;

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedSumReqRolledMetallChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK || !isEvaluateRolledMetal)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } isRequired={isRequired} label = 'Новая цена в руб./т без НДС'>
      <InputTextbox
        placeholder = 'Новая цена в руб./т без НДС'
        variant = { lookAndFeel.textboxVariant }
        value = { sumReqRolledMetall }
        valueTransformer = { (value: string) => Util.selectDigitsAndDotFromStringN_M(value, 6, 2) }
        isDisabled = { ['view', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const IsToOffsetCosts: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isToOffsetCosts = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsToOffsetCosts);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsToOffsetCostsChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK)
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Компенсировать дополнительные затраты'
      value = { isToOffsetCosts ?? false }
      isDisabled = { mode === 'view' }
      onChange = { onChangeHandler }
    />
  )
}

const AmountOfCompensation: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const alert = useAlert();

  const [guidForDelete, setGuidForDelete] = React.useState<string>('');
  const [confirmDeleteOpened, setConfirmDeleteOpened] = React.useState<boolean>(false);
  const [warningAppendOpened, setWarningAppendOpened] = React.useState<boolean>(false);
  const [warningAppendMessage, setWarningAppendMessage] = React.useState<string>('');

  const defectGuid = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintDefectGuid);

  const files = useAppSelector((store) => storeApi.inspection.edit.selectors.selectDefectFiles(store, defectGuid)) ?? [];
  const account = useAppSelector(storeApi.auth.common.selectors.selectAccount)!;
  
  const docCountAll = parseInt(account.configs.find((config) => config.code === 'CNT_ADD_COSTS')?.value ?? '3', 10);
  const docs = files.filter((file) => file.type === ADDITIONAL_COSTS && file.mode !== MODE_DEL);
  const docCountAllow = docCountAll - docs.length;
  
  const complaintMode = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedMode);
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isToOffsetCosts = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsToOffsetCosts);
  const amountOfCompensation = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedAmountOfCompensation);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedAmountOfCompensationChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }
  
  const onDocumentSelectedHandler = async (list: FileList) => {
    const listAsArray = Array.from(list);
    let warningMessage = '';

    if (list.length > docCountAllow)
    {
      warningMessage = `Вы выбрали ${list.length} документов, можно добавить только ${docCountAllow}. Лишние документы не добавлены`;
    }

    if (warningMessage !== '')
    {
      setWarningAppendMessage(warningMessage);
      setWarningAppendOpened(true);
    }

    const docList = listAsArray.length > docCountAllow ? listAsArray.slice(0, docCountAllow) : listAsArray;

    const fileProcessing = async (files: File[]): Promise<void> => {
      if (files.length > 0)
      {
        const file = files[0];
        const fileTypeByExt = Util.checkFileTypeByExt(file.name);

        if (!['PDF', 'XLSX'].includes(fileTypeByExt))
        {
          alert.error(`Файл ${file.name} не является PDF или XLSX документом`);
          return fileProcessing(files.slice(1));
        }

        const fileTypeByHeader = await Util.checkFileTypeByHeader(file);

        if (!['PDF', 'XLSX'].includes(fileTypeByHeader))
        {
          alert.error(`Файл ${file.name} не является PDF или XLSX документом`);
          return fileProcessing(files.slice(1));
        }

        if ((file.size / Math.pow(1024, 2)) > Constants.PIECE_DOC_BIGGEST_SIZE_MB)
        {
          alert.error(`Размер файла ${file.name} превышает допустимый размер прикрепляемого файла в ${Constants.PIECE_DOC_BIGGEST_SIZE_MB} Мбайт`);
          return fileProcessing(files.slice(1));
        }

        const guid = uuidv4();
        const binaryStr = await Util.fileToBinaryString(file);
        const dataUrlResult = await Util.fileToDataURL(file);

        const fileName = Util.buildDefectAttachmentName(ADDITIONAL_COSTS, file.name);

        if (dataUrlResult.ok)
        {
          dispatch(storeApi.inspection.edit.actions.defectDocumentAdded(
            {
              from: 'complaint',
              file: {
                guid,
                mode: MODE_NEW,
                name: fileName,
                type: ADDITIONAL_COSTS,
                __dataUrl: dataUrlResult.result,
                __binaryStr: binaryStr,
                __mime: file.type,
                __name: fileName
              }
            }
          ));
        }
        else
        {
          alert.error(`Произошла ошибка при обработке файла`);
        }

        return fileProcessing(files.slice(1));
      }
    }

    await fileProcessing(docList);

    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  const onAddHandler = () => {
    document.getElementById(`complaint-compensation-doc-selector`)?.click();
  }

  const onDeleteHandler = (guid: string) => {
    setConfirmDeleteOpened(true);
    setGuidForDelete(guid);
  }

  const onCancelDeleteHandler = () => {
    setConfirmDeleteOpened(false);
  }

  const onAcceptDeleteHandler = () => {
    setConfirmDeleteOpened(false);
    dispatch(storeApi.inspection.edit.actions.defectDocumentDeleted(guidForDelete));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  const onAcceptWarningAppendHandler = () => {
    setWarningAppendOpened(false);
  }

  if (complaintType === ComplaintType.REMARK || !isToOffsetCosts)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } isRequired label = 'Сумма компенсации без НДС'>
      <InputTextbox
        placeholder = 'Сумма с ед. изм.'
        variant = { lookAndFeel.textboxVariant }
        value = { amountOfCompensation }
        valueTransformer = { (value: string) => Util.selectDigitsAndDotFromStringN_M(value, 7, 2) }
        isDisabled = { mode === 'view' }
        onChange = { onChangeHandler }
      />
      <PhotoList lookAndFeel = { lookAndFeel }>
        <PhotoProvider >
          {docs.map((doc) => (
            <AttachmentDOC
              key = { doc.guid }
              variant = { lookAndFeel.attachmentDOCVariants }
              isAllowDelete = { complaintMode !== 'view' }
              doc = { doc }
              source = 'inspection'
              onDelete = { onDeleteHandler }
            />
          ))}
          {docCountAllow > 0 && complaintMode !== 'view' && (
            <AttachmentAdd
              variant = { lookAndFeel.attachmentAddVariants }
              attachmentType = { types.inspection.PieceFileType.PDF }
              label = 'Документ'
              isRequired = { docs.length === 0 }
              allowCount = { docCountAllow }
              onTap = { onAddHandler }
              onDragFiles = { onDocumentSelectedHandler }
            />
          )}
        </PhotoProvider>
      </PhotoList>
      <input
        id = 'complaint-compensation-doc-selector'
        type = 'file'
        accept = '.pdf,.xlsx'
        multiple
        onChange = { (e) => e.target.files !== null && onDocumentSelectedHandler(e.target.files) }
        onClick = { () => (document.getElementById('complaint-compensation-doc-selector') as HTMLInputElement).value = '' }
        style = { { display: 'none' } }
      />
      <TwoActionDialog 
        variant = { lookAndFeel.pdfDialogVariant } 
        opened = { confirmDeleteOpened }
        caption = 'Удалить документ?'
        onCancel = { onCancelDeleteHandler }
        onAction = { onAcceptDeleteHandler }
      />
      <TwoActionDialog 
        variant = { lookAndFeel.pdfDialogVariant } 
        opened = { warningAppendOpened }
        caption = 'Внимание'
        message = { warningAppendMessage }
        withoutCancel
        actionLabel = 'Ok'
        isDanger
        onAction = { onAcceptWarningAppendHandler }
      />
    </Labeled>
  )
}

const IsReturnOfRolledMetal: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isReturnOfRolledMetal = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsReturnOfRolledMetal);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsReturnOfRolledMetalChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK)
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Осуществить возврат металлопроката'
      value = { isReturnOfRolledMetal ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const IsSale: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isSale = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsSale);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsSaleChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK)
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Предоставить скидку на забракованную партию проката в размере'
      value = { isSale ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const Sale: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isSale = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsSale);
  const sale = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedSale);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedSaleChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK || !isSale)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } isRequired label = 'Размер скидки (%)'>
      <InputTextbox
        placeholder = '%'
        variant = { lookAndFeel.textboxVariant }
        value = { sale }
        valueTransformer = { (value: string) => Util.selectPercentFromString(value) }
        isDisabled = { ['view', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const IsRepresentative: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isRepresentative = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsRepresentative);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsRepresentativeChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK && (mode !== 'view' || !isRepresentative))
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Направить представителя'
      value = { isRepresentative ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const IsProvideResponse: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isProvideResponse = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsProvideResponse);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsProvideResponseChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK && (mode !== 'view' || !isProvideResponse))
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Предоставить оперативный ответ по действиям с забракованным металлопрокатом или возможности переработки проката'
      value = { isProvideResponse ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const IsIdentifyDangerous: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isIdentifyDangerous = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsIdentifyDangerous);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsIdentifyDangerousChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK && (mode !== 'view' || !isIdentifyDangerous))
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Определить потенциально опасные партии проката по указанному несоответствию'
      value = { isIdentifyDangerous ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const IsAnalizProblem: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isAnalizProblem = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsAnalizProblem);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsAnalizProblemChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK && (mode !== 'view' || !isAnalizProblem))
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Провести анализ проблем с целью выяснения причины дефекта'
      value = { isAnalizProblem ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const Is8d: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const is8d = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIs8d);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIs8dChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK && (mode !== 'view' || !is8d))
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Предоставить отчет в формате 8D'
      value = { is8d ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const IsAddMetall: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isAddMetall = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsAddMetall);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsAddMetallChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK && (mode !== 'view' || !isAddMetall))
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Допоставить металл'
      value = { isAddMetall ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const IsInformDeadline: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isInformDeadline = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsInformDeadline);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsInformDeadlineChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK && (mode !== 'view' || !isInformDeadline))
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Информировать о запланированном сроке исполнения заказа'
      value = { isInformDeadline ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const IsCompensatePenalty: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isCompensatePenalty = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsCompensatePenalty);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedIsCompensatePenaltyChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.REMARK)
  {
    return null;
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Компенсировать штрафную неустойку'
      value = { isCompensatePenalty ?? false }
      isDisabled = { mode === 'view' }
      onChange = { onChangeHandler }
    />
  )
}

const Penalty: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const alert = useAlert();

  const [guidForDelete, setGuidForDelete] = React.useState<string>('');
  const [confirmDeleteOpened, setConfirmDeleteOpened] = React.useState<boolean>(false);
  const [warningAppendOpened, setWarningAppendOpened] = React.useState<boolean>(false);
  const [warningAppendMessage, setWarningAppendMessage] = React.useState<string>('');

  const defectGuid = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintDefectGuid);

  const files = useAppSelector((store) => storeApi.inspection.edit.selectors.selectDefectFiles(store, defectGuid)) ?? [];
  const account = useAppSelector(storeApi.auth.common.selectors.selectAccount)!;
  
  const docCountAll = parseInt(account.configs.find((config) => config.code === 'CNT_ADD_COSTS')?.value ?? '3', 10);
  const docs = files.filter((file) => file.type === ADDITIONAL_COSTS && file.mode !== MODE_DEL);
  const docCountAllow = docCountAll - docs.length;
  
  const complaintMode = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedMode);
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const isCompensatePenalty = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsCompensatePenalty);
  const penalty = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedPenalty);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedPenaltyChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }
  
  const onDocumentSelectedHandler = async (list: FileList) => {
    const listAsArray = Array.from(list);
    let warningMessage = '';

    if (list.length > docCountAllow)
    {
      warningMessage = `Вы выбрали ${list.length} документов, можно добавить только ${docCountAllow}. Лишние документы не добавлены`;
    }

    if (warningMessage !== '')
    {
      setWarningAppendMessage(warningMessage);
      setWarningAppendOpened(true);
    }

    const docList = listAsArray.length > docCountAllow ? listAsArray.slice(0, docCountAllow) : listAsArray;

    const fileProcessing = async (files: File[]): Promise<void> => {
      if (files.length > 0)
      {
        const file = files[0];
        const fileTypeByExt = Util.checkFileTypeByExt(file.name);

        if (!['PDF', 'XLSX'].includes(fileTypeByExt))
        {
          alert.error(`Файл ${file.name} не является PDF или XLSX документом`);
          return fileProcessing(files.slice(1));
        }

        const fileTypeByHeader = await Util.checkFileTypeByHeader(file);

        if (!['PDF', 'XLSX'].includes(fileTypeByHeader))
        {
          alert.error(`Файл ${file.name} не является PDF или XLSX документом`);
          return fileProcessing(files.slice(1));
        }

        if ((file.size / Math.pow(1024, 2)) > Constants.PIECE_DOC_BIGGEST_SIZE_MB)
        {
          alert.error(`Размер файла ${file.name} превышает допустимый размер прикрепляемого файла в ${Constants.PIECE_DOC_BIGGEST_SIZE_MB} Мбайт`);
          return fileProcessing(files.slice(1));
        }

        const guid = uuidv4();
        const binaryStr = await Util.fileToBinaryString(file);
        const dataUrlResult = await Util.fileToDataURL(file);

        const fileName = Util.buildDefectAttachmentName(ADDITIONAL_COSTS, file.name);

        if (dataUrlResult.ok)
        {
          dispatch(storeApi.inspection.edit.actions.defectDocumentAdded(
            {
              from: 'complaint',
              file: {
                guid,
                mode: MODE_NEW,
                name: fileName,
                type: ADDITIONAL_COSTS,
                __dataUrl: dataUrlResult.result,
                __binaryStr: binaryStr,
                __mime: file.type,
                __name: fileName
              }
            }
          ));
        }
        else
        {
          alert.error(`Произошла ошибка при обработке файла`);
        }

        return fileProcessing(files.slice(1));
      }
    }

    await fileProcessing(docList);

    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  const onAddHandler = () => {
    document.getElementById(`complaint-penalty-doc-selector`)?.click();
  }

  const onDeleteHandler = (guid: string) => {
    setConfirmDeleteOpened(true);
    setGuidForDelete(guid);
  }

  const onCancelDeleteHandler = () => {
    setConfirmDeleteOpened(false);
  }

  const onAcceptDeleteHandler = () => {
    setConfirmDeleteOpened(false);
    dispatch(storeApi.inspection.edit.actions.defectDocumentDeleted(guidForDelete));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  const onAcceptWarningAppendHandler = () => {
    setWarningAppendOpened(false);
  }

  if (complaintType === ComplaintType.REMARK || !isCompensatePenalty)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } isRequired label = 'Размер штрафной неустойки'>
      <InputTextbox
        placeholder = 'Введите значение'
        variant = { lookAndFeel.textboxVariant }
        value = { penalty }
        valueTransformer = { (value: string) => Util.selectDigitsAndDotFromStringN_M(value, 7, 2) }
        isDisabled = { mode === 'view' }
        onChange = { onChangeHandler }
      />
      <PhotoList lookAndFeel = { lookAndFeel }>
        <PhotoProvider >
          {docs.map((doc) => (
            <AttachmentDOC
              key = { doc.guid }
              variant = { lookAndFeel.attachmentDOCVariants }
              isAllowDelete = { complaintMode !== 'view' }
              doc = { doc }
              source = 'inspection'
              onDelete = { onDeleteHandler }
            />
          ))}
          {docCountAllow > 0 && complaintMode !== 'view' && (
            <AttachmentAdd
              variant = { lookAndFeel.attachmentAddVariants }
              attachmentType = { types.inspection.PieceFileType.PDF }
              label = 'Документ'
              isRequired = { docs.length === 0 }
              allowCount = { docCountAllow }
              onTap = { onAddHandler }
              onDragFiles = { onDocumentSelectedHandler }
            />
          )}
        </PhotoProvider>
      </PhotoList>
      <input
        id = 'complaint-penalty-doc-selector'
        type = 'file'
        accept = '.pdf,.xlsx'
        multiple
        onChange = { (e) => e.target.files !== null && onDocumentSelectedHandler(e.target.files) }
        onClick = { () => (document.getElementById('complaint-penalty-doc-selector') as HTMLInputElement).value = '' }
        style = { { display: 'none' } }
      />
      <TwoActionDialog 
        variant = { lookAndFeel.pdfDialogVariant }
        opened = { confirmDeleteOpened }
        caption = 'Удалить документ?'
        onCancel = { onCancelDeleteHandler }
        onAction = { onAcceptDeleteHandler }
      />
      <TwoActionDialog 
        variant = { lookAndFeel.pdfDialogVariant }
        opened = { warningAppendOpened }
        caption = 'Внимание'
        message = { warningAppendMessage }
        withoutCancel
        actionLabel = 'Ok'
        isDanger
        onAction = { onAcceptWarningAppendHandler }
      />
    </Labeled>
  )
}

const OtherReq: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const complaintType = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const otherReq = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedOtherReq);
  const isOtherReqVisible = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsOtherReqVisible);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedOtherReqChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (complaintType === ComplaintType.COMPLAINT && !isOtherReqVisible)
  {
    return null;
  }

  return (
    <Labeled
      variant = { lookAndFeel.labeledVariant }
      label = { complaintType === ComplaintType.REMARK ? 'Другие требования' : 'Комментарий к требованию' }
    >
      <InputMultiline
        variant = { lookAndFeel.multilineVariant }
        value = { otherReq ?? '' }
        maxLength = { 2000 }
        isDisabled = { mode === 'view' }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const InitiatorAOSDSelector: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const [referOpened, setReferOpened] = React.useState<boolean>(false);

  const isToScrap = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsToScrap);
  const initiatorAOSDSelected = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedInitiatorAOSDSelected);
  const account = useAppSelector(storeApi.auth.common.selectors.selectAccount)!;
  const isAOSD = account.configs.find((item) => item.code === 'IND_DISTRIBUTION')!.value === '1';
  const userFilialId = useAppSelector(storeApi.inspection.edit.selectors.selectGeneralUserFilialId);

  const items = [
    {
      id: 'Y',
      label: 'Да',
    },
    {
      id: 'N',
      label: 'Нет',
    }
  ];

  const onItemSelectedHandler = (item: ReferSelectorItem) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedInitiatorAOSDSelectedChanged(item.id === 'Y')); 
    
    if (item.id === 'N' && isToScrap)
    {
      dispatch(storeApi.inspection.edit.async.replaceScrapPriceAsync({
        enterpriseId: null,
        filialId: (userFilialId ?? 0) > 0 ? userFilialId : null,
      }))
        .unwrap()
        .then(() => {
          setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
        })
        .catch(() => {
          setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
        })
    }
    else
    {
      setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
    }
  }

  if (!isAOSD)
  {
    return null;
  }

  return (
    <React.Fragment>
      <Labeled variant = { lookAndFeel.labeledVariant } label = 'Обращение от конечного клиента?' isRequired>
        <InputTextbox
          variant = { lookAndFeel.textboxVariant }
          value = { initiatorAOSDSelected ? 'Да' : 'Нет' }
          readOnly
          isDisabled = { ['view', 'tocomplaint', 'tocomplaintfinance', 'costs'].includes(mode)}
          onChange = { voidFunction }
          placeholder = 'Выбрать'
          customIcon = { <ArrowDownVector /> }
          onCustomIconTap = { () => setReferOpened(true) }
        />
      </Labeled>
      <ReferSelector
        variant = { lookAndFeel.referSelectorVariant }
        referItemsList = { items }
        isOpened = { referOpened }
        label = 'Обращение от конечного клиента'
        selectedId = { initiatorAOSDSelected ? 'Y' : 'N' }
        onClose = { () => setReferOpened(false) }
        onSelect = { onItemSelectedHandler }
      />
    </React.Fragment>
  )
}

const InitiatorEnterpriseId: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const [referOpened, setReferOpened] = React.useState<boolean>(false);

  const isToScrap = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedIsToScrap);
  const enterpriseId = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedInitiatorEnterpriseId);
  const enterpriseAll = useAppSelector(storeApi.inspection.edit.selectors.selectGeneralEnterpriseRefer);
  const enterpriseStr = enterpriseAll.find((item) => item.id === enterpriseId)?.title ?? '';

  const items = enterpriseAll.map((item) => ({
    id: `${item.id}`,
    label: item.title,
    sublabel: item.inn,
  }))

  const onItemSelectedHandler = (item: ReferSelectorItem) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedInitiatorEnterpriseIdChanged(parseInt(item.id, 10)));

    if (isToScrap)
    {
      dispatch(storeApi.inspection.edit.async.replaceScrapPriceAsync({
        enterpriseId: parseInt(item.id, 10),
        filialId: null,
      }))
        .unwrap()
        .then(() => {
          setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
        })
        .catch(() => {
          setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
        })
    }
    else
    {
      setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
    }
  }

  return (
    <React.Fragment>
      <Labeled variant = { lookAndFeel.labeledVariant } label = 'Предприятие' isRequired>
        <InputTextbox
          variant = { lookAndFeel.textboxVariant }
          value = { enterpriseStr }
          readOnly
          isDisabled = { ['view', 'tocomplaint', 'tocomplaintfinance', 'costs'].includes(mode)}
          onChange = { voidFunction }
          placeholder = 'Выбрать'
          customIcon = { <ArrowDownVector /> }
          onCustomIconTap = { () => setReferOpened(true) }
        />
      </Labeled>
      <ReferSelector
        variant = { lookAndFeel.referSelectorVariant }
        referItemsList = { items }
        isOpened = { referOpened }
        label = 'Предприятие'
        selectedId = { enterpriseId > -1 ? `${enterpriseId}` : null }
        onClose = { () => setReferOpened(false) }
        onSelect = { onItemSelectedHandler }
      />
    </React.Fragment>
  )
}

const InitiatorFIO: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const initiatorFIO = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedInitiatorFIO);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedInitiatorFIOChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'ФИО' isRequired>
      <InputMultiline
        variant = { lookAndFeel.multilineVariant }
        value = { initiatorFIO ?? '' }
        maxLength = { 250 }
        isDisabled = { ['view', 'tocomplaint', 'tocomplaintfinance', 'costs'].includes(mode)}
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const InitiatorPost: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const post = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedInitiatorPost);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedInitiatorPostChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Должность'>
      <InputMultiline
        variant = { lookAndFeel.multilineVariant }
        value = { post ?? '' }
        maxLength = { 125 }
        isDisabled = { ['view', 'tocomplaint', 'tocomplaintfinance', 'costs'].includes(mode)}
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const InitiatorPhone: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const phone = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedInitiatorPhone);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedInitiatorPhoneChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  return (
      <Labeled variant = { lookAndFeel.labeledVariant } label = 'Номер телефона'>
        <InputPhone
          variant = { lookAndFeel.inputPhoneVariant }
          value = { `${phone ?? ''}` }
          isDisabled = { ['view', 'tocomplaint', 'tocomplaintfinance', 'costs'].includes(mode)}
          onChange = { onChangeHandler }
        />
      </Labeled>
  )
}

const InitiatorEmail: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const email = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedInitiatorEmail);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedInitiatorEmailChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'E-mail' isRequired>
      <InputMultiline
        variant = { lookAndFeel.multilineVariant }
        value = { email ?? '' }
        maxLength = { 50 }
        isDisabled = { ['view', 'tocomplaint', 'tocomplaintfinance', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const IsContactDataExpanded: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const contactDataExpanded = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedContactDataExpanded);

  const onChangeHandler = (value: boolean) => {
    dispatch(storeApi.inspection.edit.actions.complaintContactDataExpandedChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  return (
    <Checkbox
      variant = 'h24bluetext'
      label = 'Данные контактного лица представителя'
      value = { contactDataExpanded ?? false }
      isDisabled = { ['view', 'costs'].includes(mode) }
      onChange = { onChangeHandler }
    />
  )
}

const FIO: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const contactDataExpanded = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedContactDataExpanded);
  const fio = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedFIO);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedFIOChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (!contactDataExpanded)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'ФИО'>
      <InputMultiline
        variant = { lookAndFeel.multilineVariant }
        value = { fio ?? '' }
        isDisabled = { ['view', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const Enterprise: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const contactDataExpanded = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedContactDataExpanded);
  const enterprise = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedEnterprise);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedEnterpriseChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (!contactDataExpanded)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Организация'>
      <InputMultiline
        variant = { lookAndFeel.multilineVariant }
        value = { enterprise ?? '' }
        isDisabled = { ['view', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const Phone: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const contactDataExpanded = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedContactDataExpanded);
  const phone = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedPhone);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedPhoneChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (!contactDataExpanded)
  {
    return null;
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Телефон'>
      <InputTextbox
        variant = { lookAndFeel.textboxVariant }
        value = { phone ?? '' }
        isDisabled = { ['view', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const Email: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const [deleteIndex, setDeleteIndex] = React.useState<number>(-1);

  const contactDataExpanded = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedContactDataExpanded);
  const emailList = (useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedEmail) ?? '').split(';');

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedEmailChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  if (!contactDataExpanded)
  {
    return null;
  }

  return (
    <React.Fragment>
      {emailList.map((email, index) => (
        <RowWithAction key = { index } lookAndFeel = { lookAndFeel }>
          <Labeled variant = { lookAndFeel.labeledVariant } label = 'E-mail'>
            <InputTextbox
              variant = { lookAndFeel.textboxVariant }
              value = { email ?? '' }
              isDisabled = { ['view', 'costs'].includes(mode) }
              onChange = {
                (value: string) => onChangeHandler(
                  emailList.map((item, itemIndex) => itemIndex === index ? value : item).join(';')
                )
              }
            />
          </Labeled>
          {emailList.length > 1 && (
            <IconButton
              variant = { lookAndFeel.headerIconButtonVariant }
              isTapAllowed
              onTap = {
                () => {
                  setDeleteIndex(index);
                }
              }
            >
              <TrashVector />
            </IconButton>
          )}
        </RowWithAction>
      ))}
      {emailList.every((email) => email.trim().length > 0) && (
        <RowWithAction lookAndFeel = { lookAndFeel }>
          <Button
            variant = { lookAndFeel.submitButtonVariant }
            onTap = {
              () =>  onChangeHandler([...emailList, ''].join(';'))
            }
            label = { 'Добавить E-mail' }
          />
        </RowWithAction>
      )}
      <TwoActionDialog 
        variant = { lookAndFeel.twoActionDialogVariant }
        opened = { deleteIndex > -1 }
        caption = ' Удалить  E-mail?'
        cancelLabel = 'Отмена'
        actionLabel = 'Удалить'
        onCancel = { () => setDeleteIndex(-1) }
        onAction = {
          () => {
            emailList.splice(deleteIndex, 1);
            onChangeHandler(emailList.join(';'));
            setDeleteIndex(-1);
          }
        }
      />
    </React.Fragment>
  )
}

const GroupRevised: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const groupRevised = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedGroupRevised);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedGroupRevisedChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Переработанные партии без дефектов'>
      <InputMultiline
        variant = { lookAndFeel.multilineVariant }
        placeholder = '№'
        value = { groupRevised ?? '' }
        isDisabled = { ['view', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const GroupRevisedValue: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const groupRevisedValue = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedGroupRevisedValue);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedGroupRevisedValueChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Объем (т)'>
      <InputTextbox
        variant = { lookAndFeel.textboxVariant }
        placeholder = 'Тонн'
        value = { `${groupRevisedValue ?? ''}` }
        valueTransformer = { Util.selectDigitsFromString }
        isDisabled = { ['view', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const GroupUnrevised: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const groupUnrevised = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedGroupUnrevised);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedGroupUnrevisedChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Не переработанные партии на складе'>
      <InputMultiline
        variant = { lookAndFeel.multilineVariant }
        placeholder = '№'
        value = { groupUnrevised ?? '' }
        isDisabled = { ['view', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const GroupUnrevisedValue: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const groupUnrevisedValue = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedGroupUnrevisedValue);

  const onChangeHandler = (value: string) => {
    dispatch(storeApi.inspection.edit.actions.complaintEditedGroupUnrevisedValueChanged(value));
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE)), 200);
  }

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Объем (т)'>
      <InputTextbox
        variant = { lookAndFeel.textboxVariant }
        placeholder = 'Тонн'
        value = { `${groupUnrevisedValue ?? ''}` }
        valueTransformer = { Util.selectDigitsFromString }
        isDisabled = { ['view', 'costs'].includes(mode) }
        onChange = { onChangeHandler }
      />
    </Labeled>
  )
}

const Decision: React.FC<ComplaintEditFormElement> = ({ lookAndFeel, dispatch, mode }) => {
  const decision = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedDecision);

  return (
    <Labeled variant = { lookAndFeel.labeledVariant } label = 'Решение по забракованной продукции'>
      <InputMultiline
        variant = { lookAndFeel.multilineVariant }
        value = { decision ?? '' }
        isDisabled
        onChange = { voidFunction }
      />
    </Labeled>
  )
}


type Props = {
  variant: ComplaintEditVariants;
}
export const ComplaintEdit: React.FC<Props> = ({
  variant,
}) => {
  const lookAndFeel = complaintedit_variants.get(variant)!;
  const dispatch = useAppDispatch();

  const [requestSendOpened, setRequestSendOpened] = React.useState<boolean>(false);
  const [submitErrorOpened, setSubmitErrorOpened] = React.useState<boolean>(false);
  const [closeConfirmOpened, setCloseConfirmOpened] = React.useState<boolean>(false);
  const [submitError, setSubmitError] = React.useState<string>('');

  const isOpened = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditOpened);
  const mode = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedMode);
  const isGroup = useAppSelector(storeApi.inspection.edit.selectors.selectGeneralIsGroup);
  const typeComplaint = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedTypeComplaint);
  const defectGuid = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintDefectGuid);
  const defectTypeComplaint = useAppSelector((store) => storeApi.inspection.edit.selectors.selectDefectComplaintType(store, defectGuid));
  const initiatorAOSDSelected = useAppSelector(storeApi.inspection.edit.selectors.selectComplaintEditedInitiatorAOSDSelected);
  const account = useAppSelector(storeApi.auth.common.selectors.selectAccount)!;

  const defectType = useAppSelector((store) => storeApi.inspection.edit.selectors.selectDefectDefectType(store, defectGuid));

  const isQualityOrService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(defectType);
  const isDirtyWagons = defectType === DefectType.DIRTY_WAGONS;
  const isTerm = defectType === DefectType.TERM;

  const closeComplaintEdit = (isSend?: boolean) => {
    if (isGroup)
    {
      dispatch(storeApi.inspection.edit.actions.complaintEditClosed());
    }
    else
    {
      Urls.InspectionReturnFromComplaint.build(isSend).navigate();
    }
  }

  const onActionSubmitErrorHandler = () => {
    if (submitError === 'Изменения отсутствуют')
    {
      closeComplaintEdit();
    }
    setSubmitErrorOpened(false);
    setSubmitError('');
  }

  const onRequestSaveAcceptHandler = () => {
    setTimeout(
      () => dispatch(
        storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_TRUE_APPLY)
      )
        .unwrap()
        .then(() => {
          setCloseConfirmOpened(false);
          closeComplaintEdit();
        }),
      200
    );
  }

  const onRequestSaveCancelHandler = () => {
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_FALSE)), 200);
    setCloseConfirmOpened(false);
    closeComplaintEdit();
  }

  const onSubmitHandler = () => {
    dispatch(storeApi.inspection.edit.async.checkComplaintBeforeAddAsync())
      .unwrap()
      .then(() => {
        if (typeComplaint === ComplaintType.REMARK)
        {
          if (account.role === AccountRole.ClientAdmin)
          {
            setRequestSendOpened(true);
          }
          else
          {
            submit(account.role === AccountRole.Stpk);
          }
        }
        else
        {
          submit(false);
        }
      })
      .catch((error) => {
        setSubmitError(error);
        setSubmitErrorOpened(true);
      });
  }

  const onRequestSendCancelHandler = () => {
    setRequestSendOpened(false);
    submit(false);
  }

  const onRequestSendAcceptHandler = () => {
    setRequestSendOpened(false);
    submit(true);
  }

  const submit = (isTransferImmediately: boolean) => {
    dispatch(storeApi.inspection.edit.async.addComplaintAsync({ isTransferImmediately }))
      .unwrap()
      .then((result) => {
        setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_FALSE)), 200);
        closeComplaintEdit(['tocomplaint', 'tocomplaintfinance', 'costs'].includes(mode));
      })
      .catch((error) => {
        setSubmitError(error);
        setSubmitErrorOpened(true);
      });
  }

  const closeWithCheckChanges = () => {
    dispatch(storeApi.inspection.edit.async.closeComplaintAsync())
      .unwrap()
      .then((result) => {
        if (result.hasChanges)
        {
          setCloseConfirmOpened(true);
        }
        else
        {
          closeWithoutCheckChanges();
        }
      });
  }

  const closeWithoutCheckChanges = () => {
    setTimeout(() => dispatch(storeApi.inspection.edit.async.saveImmediatelyAsync(SAVE_NOW_FALSE)), 200);
    closeComplaintEdit();
  }

  if (!isOpened)
  {
    return null;
  }

  return (
    <Drawer
      anchor = 'bottom'
      open = { isOpened }
      onClose = { closeWithCheckChanges }
    >
      <Container lookAndFeel = { lookAndFeel }>
        <HeaderContainer lookAndFeel = { lookAndFeel }>
          <IconButton
            variant = { lookAndFeel.headerIconButtonVariant }
            isTapAllowed
            onTap = { closeWithCheckChanges }
          >
            <ArrowBackVector />
          </IconButton>
          <Typography variant = { lookAndFeel.headerTypographyVariant }>
            { mode === 'view' ? 'Требование' : (mode === 'new' ? 'Добавление требования' : 'Изменение требования') }
          </Typography>
          <Grow size = { 2 } />
          <IconButton
            variant = { lookAndFeel.headerIconButtonVariant }
            isTapAllowed
            onTap = { closeWithCheckChanges }
          >
            <CloseOutlineVectorBig />
          </IconButton>
        </HeaderContainer>
        <FormContainer lookAndFeel = { lookAndFeel }>
          <Type lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
          <Typography variant = 'blackw600s18lh24'>Требования и запрашиваемые действия к поставщику</Typography>
          {(isQualityOrService || isDirtyWagons) && (
            <React.Fragment>
              <IsToScrap lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <ScrapPrice lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <IsEvaluateRolledMetal lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <RequirementRolledMetall lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <SumReqRolledMetall lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <IsToOffsetCosts lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <AmountOfCompensation lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <IsReturnOfRolledMetal lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <IsSale lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <Sale lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <IsRepresentative lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <IsProvideResponse lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              {!isDirtyWagons && (
                <IsIdentifyDangerous lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              )}
              <IsAnalizProblem lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              {!isDirtyWagons && (
                <Is8d lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              )}
            </React.Fragment>
          )}
          {isTerm && (
            <React.Fragment>
              <IsAddMetall lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <IsInformDeadline lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <IsEvaluateRolledMetal lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <RequirementRolledMetall lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <SumReqRolledMetall lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <IsCompensatePenalty lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <Penalty lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <IsSale lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <Sale lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
            </React.Fragment>
          )}
          <OtherReq lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
          <Hr lookAndFeel = { lookAndFeel } />
          <InitiatorAOSDSelector lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
          {((account.role === AccountRole.Stpk) || initiatorAOSDSelected) && (
            <React.Fragment>
              <Typography variant = 'blackw600s18lh24'>Данные инициатора</Typography>
              <InitiatorEnterpriseId lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <InitiatorFIO lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <InitiatorPost lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <InitiatorPhone lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <InitiatorEmail lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <Hr lookAndFeel = { lookAndFeel } />
            </React.Fragment>
          )}
          {(isQualityOrService || isDirtyWagons) && (
            <React.Fragment>
              <IsContactDataExpanded lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <FIO lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <Enterprise lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <Phone lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <Email lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <Hr lookAndFeel = { lookAndFeel } />
            </React.Fragment>
          )}
          {isQualityOrService && (
            <React.Fragment>
              <Typography variant = 'blackw600s18lh24'>Информация</Typography>
              <GroupRevised lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <GroupRevisedValue lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <GroupUnrevised lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <GroupUnrevisedValue lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
              <Decision lookAndFeel = { lookAndFeel } dispatch = { dispatch } mode = { mode } />
            </React.Fragment>
          )}
          {mode !== 'view' && (
            <SubmitContainer lookAndFeel = { lookAndFeel }>
              <Button
                variant = { lookAndFeel.cancelButtonVariant }
                onTap = { closeWithoutCheckChanges }
                label = 'Отмена'
              />
              <Button
                variant = { lookAndFeel.submitButtonVariant }
                onTap = { onSubmitHandler }
                label = { defectTypeComplaint === null ? 'Добавить' : 'Изменить' }
              />
            </SubmitContainer>
          )}
        </FormContainer>
        <TwoActionDialog 
          variant = { lookAndFeel.twoActionDialogVariant }
          opened = { requestSendOpened }
          caption = 'Передать на Северсталь для рассмотрения?'
          cancelLabel = 'Отмена'
          actionLabel = 'Передать'
          onCancel = { onRequestSendCancelHandler }
          onAction = { onRequestSendAcceptHandler }
        />
        <TwoActionDialog 
          variant = { lookAndFeel.twoActionDialogVariant }
          opened = { submitErrorOpened }
          caption = 'Ошибка'
          message = { submitError }
          isDanger
          withoutCancel
          onAction = { onActionSubmitErrorHandler }
        />
        <TwoActionDialog 
          variant = { lookAndFeel.twoActionDialogVariant } 
          opened = { closeConfirmOpened }
          caption = 'Сохранить информацию?'
          message = 'Требования будут сохранены без проверки на обязательность заполнения полей'
          isDanger
          cancelLabel = 'Отмена'
          actionLabel = 'Сохранить'
          onAction = { onRequestSaveAcceptHandler }
          onCancel = { onRequestSaveCancelHandler }
        />
      </Container>
    </Drawer>
  );
};
